import * as React from "react";
import { Mail, Phone } from "lucide-react";
import { Brand, cn } from "../../ui";
import AnchorLogo from "../../../assets/anchorLogo.png";
import { SmartLink } from "./SiteHeader";
import {
  AUDIENCE_LINKS,
  COMPANY_LINKS,
  LEGAL_LINKS,
  PLATFORM_DISCLAIMER,
  SUPPORT_EMAIL,
  SUPPORT_PHONE,
  type SiteLink,
} from "./siteConfig";

export interface FooterColumn {
  title: string;
  links: SiteLink[];
}

export interface SiteFooterProps {
  columns?: FooterColumn[];
  phone?: { display: string; href: string };
  /** Shows the email row; clicking calls this instead of mailto when provided */
  onEmailClick?: () => void;
  /** Anchor Loans partner block */
  showPartner?: boolean;
  className?: string;
}

const DEFAULT_COLUMNS: FooterColumn[] = [
  { title: "For you", links: AUDIENCE_LINKS.map(({ label, href }) => ({ label, href })) },
  { title: "Company", links: COMPANY_LINKS },
  { title: "Products", links: [{ label: "Tools for pros", href: "/tools-for-pros" }, { label: "Browse properties", href: "/marketplace" }] },
];

/** Shared marketing footer: brand + columns + partner/disclaimer + legal bar */
export const SiteFooter: React.FC<SiteFooterProps> = ({
  columns = DEFAULT_COLUMNS,
  phone = SUPPORT_PHONE,
  onEmailClick,
  showPartner = true,
  className,
}) => {
  const year = new Date().getFullYear();
  const linkClass = "text-sm text-gray-600 transition-colors hover:text-gray-900";

  return (
    <footer className={cn("border-t border-gray-200 bg-white", className)}>
      <div className="mx-auto w-full max-w-7xl px-4 py-14 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-12">
          <div className="space-y-5 lg:col-span-4">
            <Brand />
            <p className="max-w-xs text-sm leading-6 text-gray-600">
              The community-driven marketplace for validated off-market real estate and verified buyers.
            </p>
            <ul className="space-y-2.5">
              <li>
                <a href={phone.href} className={cn(linkClass, "inline-flex items-center gap-2")}>
                  <Phone className="size-4 text-gray-400" />
                  <span className="tabular">{phone.display}</span>
                </a>
              </li>
              <li>
                {onEmailClick ? (
                  <button type="button" onClick={onEmailClick} className={cn(linkClass, "inline-flex items-center gap-2")}>
                    <Mail className="size-4 text-gray-400" />
                    {SUPPORT_EMAIL}
                  </button>
                ) : (
                  <a href={`mailto:${SUPPORT_EMAIL}`} className={cn(linkClass, "inline-flex items-center gap-2")}>
                    <Mail className="size-4 text-gray-400" />
                    {SUPPORT_EMAIL}
                  </a>
                )}
              </li>
            </ul>
          </div>

          <div className="grid grid-cols-2 gap-8 sm:grid-cols-3 lg:col-span-8">
            {columns.map((col) => (
              <div key={col.title}>
                <h3 className="text-meta font-semibold text-gray-900">{col.title}</h3>
                <ul className="mt-4 space-y-3">
                  {col.links.map((link) => (
                    <li key={link.href + link.label}>
                      <SmartLink href={link.href} external={link.external} className={linkClass}>
                        {link.label}
                      </SmartLink>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {showPartner && (
          <div className="mt-12 grid gap-6 rounded-2xl bg-canvas p-5 ring-1 ring-gray-200/80 sm:p-6 lg:grid-cols-12 lg:items-center">
            <div className="flex items-center gap-4 lg:col-span-4">
              <span className="flex h-14 w-28 shrink-0 items-center justify-center rounded-xl bg-white px-3 ring-1 ring-gray-200">
                <img src={AnchorLogo} alt="Anchor Loans" className="max-h-9 w-auto object-contain" />
              </span>
              <div>
                <p className="text-meta font-medium text-gray-900">Proud to work with market leaders</p>
                <p className="tabular mt-0.5 text-xs text-gray-500">Anchor Loans, NMLS ID 2289894</p>
              </div>
            </div>
            <p className="text-xs leading-5 text-gray-500 lg:col-span-8">{PLATFORM_DISCLAIMER}</p>
          </div>
        )}
      </div>

      <div className="border-t border-gray-100">
        <div className="mx-auto flex w-full max-w-7xl flex-col gap-3 px-4 py-6 text-xs text-gray-500 sm:flex-row sm:items-center sm:justify-between sm:px-6 lg:px-8">
          <p>
            © {year} FlipIQ. All rights reserved. FlipIQ, our logo, and our other trademarks may not be used without our
            express written permission.
          </p>
          <ul className="flex shrink-0 gap-5">
            {LEGAL_LINKS.map((l) => (
              <li key={l.href}>
                <SmartLink href={l.href} className="transition-colors hover:text-gray-900">
                  {l.label}
                </SmartLink>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </footer>
  );
};
